import { useEffect, useMemo, useRef, useState } from "react";
import { Link, NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../api/client";
import type { FieldSurvey, Me, Survey } from "../api/types";
import { Icon, type IconName } from "../components/Icon";
import { LogoMark, Wordmark } from "../components/Logo";
import { ThemeToggle } from "../components/ThemeToggle";
import { ROLE_LABEL } from "../lib/format";
import { can } from "./auth";

interface NavItem { to: string; label: string; icon: IconName; show: boolean; badge?: number | null; end?: boolean }

function useReviewCount(enabled: boolean) {
  return useQuery({
    queryKey: ["review", "count"],
    queryFn: () => api<{ open: number }>("/api/review/count"),
    enabled,
    refetchInterval: 60_000,
  });
}

function SideNav({ me, open, onNavigate }: { me: Me; open: boolean; onNavigate: () => void }) {
  const review = useReviewCount(can.review(me.role));
  const groups: { label: string; items: NavItem[] }[] = [
    {
      label: "Roads",
      items: [
        { to: "/", label: "Overview", icon: "overview", show: true, end: true },
        { to: "/roads", label: "Roads", icon: "roads", show: true },
        { to: "/network/condition", label: "Network", icon: "network", show: true },
        { to: "/defects", label: "Defects", icon: "defects", show: true },
        { to: "/reports", label: "Reports", icon: "reports", show: true },
      ],
    },
    {
      label: "Work",
      items: [
        { to: "/review", label: "Review queue", icon: "review", show: can.review(me.role), badge: review.data?.open },
        { to: "/surveys", label: "Field surveys", icon: "surveys", show: can.field(me.role) },
      ],
    },
    { label: "System", items: [{ to: "/admin", label: "Admin", icon: "admin", show: can.admin(me.role) }] },
  ];
  return (
    <nav className={`sidenav${open ? " open" : ""}`} aria-label="Main">
      <Link to="/" className="brand" onClick={onNavigate}>
        <LogoMark className="brand-mark" />
        <span className="brand-text">
          <Wordmark />
          <span className="brand-sub" style={{ display: "block" }}>Road condition, inventory &amp; safety</span>
        </span>
      </Link>
      {groups.map((g) => {
        const items = g.items.filter((i) => i.show);
        if (!items.length) return null;
        return (
          <div className="nav-group" key={g.label}>
            <span className="nav-label">{g.label}</span>
            {items.map((i) => (
              <NavLink key={i.to} to={i.to} end={i.end} onClick={onNavigate}
                       className={({ isActive }) => `nav-link${isActive || (i.to.startsWith("/network") && location.pathname.startsWith("/network")) ? " active" : ""}`}>
                <Icon name={i.icon} />
                <span>{i.label}</span>
                {i.badge ? <span className="nav-badge" aria-label={`${i.badge} waiting`}>{i.badge}</span> : null}
              </NavLink>
            ))}
          </div>
        );
      })}
      <div className="nav-foot">Data-driven roads. Safer Kenya.</div>
    </nav>
  );
}

function GlobalSearch() {
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const nav = useNavigate();
  const wrap = useRef<HTMLDivElement>(null);
  const roads = useQuery({ queryKey: ["roads"], queryFn: () => api<{ items: Survey[] }>("/api/roads"), enabled: open || q.length > 0, staleTime: 60_000 });

  const results = useMemo(() => {
    const t = q.trim().toLowerCase();
    if (!t || !roads.data) return [];
    return roads.data.items
      .filter((r) => [r.road_code, r.road_name, r.county, r.agency, r.start_name, r.end_name].join(" ").toLowerCase().includes(t))
      .slice(0, 8);
  }, [q, roads.data]);

  useEffect(() => {
    const close = (e: MouseEvent) => { if (!wrap.current?.contains(e.target as Node)) setOpen(false); };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, []);

  const go = (r: Survey) => { setOpen(false); setQ(""); nav(`/roads/${r.survey_uuid}`); };

  return (
    <div className="topbar-search" ref={wrap}>
      <label htmlFor="global-search" className="sr-only">Search roads</label>
      <div className="row" style={{ position: "relative", flexWrap: "nowrap" }}>
        <span style={{ position: "absolute", left: 10, color: "var(--muted)", display: "flex" }}><Icon name="search" size={16} /></span>
        <input
          id="global-search" className="input" style={{ paddingLeft: 34 }} placeholder="Search road code, name, county…"
          role="combobox" aria-expanded={open && q.length > 0} aria-controls="search-results" autoComplete="off"
          value={q}
          onChange={(e) => { setQ(e.target.value); setOpen(true); setActive(0); }}
          onFocus={() => setOpen(true)}
          onKeyDown={(e) => {
            if (e.key === "ArrowDown") { e.preventDefault(); setActive((a) => Math.min(a + 1, results.length - 1)); }
            if (e.key === "ArrowUp") { e.preventDefault(); setActive((a) => Math.max(a - 1, 0)); }
            if (e.key === "Enter" && results[active]) go(results[active]);
            if (e.key === "Escape") setOpen(false);
          }}
        />
      </div>
      {open && q.trim() ? (
        <div className="popover search-results" id="search-results" role="listbox">
          {roads.isPending ? <p className="muted" style={{ padding: 12 }}>Searching…</p> : results.length ? (
            <>
              <div className="search-group">Roads</div>
              {results.map((r, i) => (
                <button key={r.survey_uuid} role="option" aria-selected={i === active} className="menu-item" style={{ height: "auto", padding: "8px 12px" }}
                        onMouseEnter={() => setActive(i)} onClick={() => go(r)}>
                  <span style={{ minWidth: 0 }}>
                    <b>{r.road_code}</b> — {r.road_name}
                    <small className="muted" style={{ display: "block" }}>{[r.agency, r.county, `${r.length_km.toFixed(1)} km`, r.survey_date].filter(Boolean).join(" · ")}</small>
                  </span>
                </button>
              ))}
            </>
          ) : <p className="muted" style={{ padding: 12 }}>No road matches "{q}".</p>}
        </div>
      ) : null}
    </div>
  );
}

function SurveyStatus({ me }: { me: Me }) {
  const enabled = can.field(me.role);
  const s = useQuery({ queryKey: ["surveys"], queryFn: () => api<{ active: FieldSurvey[] }>("/api/surveys"), enabled, refetchInterval: 30_000 });
  if (!enabled) return null;
  const n = s.data?.active.length ?? 0;
  const live = s.data?.active.some((a) => (a.last_fix_age_s ?? 1e9) < 300);
  return (
    <Link to="/surveys" className="status hide-phone" title="Surveys being captured">
      <span className={`status-dot${live ? " live" : ""}`} />
      {s.isPending ? "Surveys…" : n ? `${n} active survey${n > 1 ? "s" : ""}` : "No active surveys"}
    </Link>
  );
}

function UserMenu({ me }: { me: Me }) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const qc = useQueryClient();
  const nav = useNavigate();
  useEffect(() => {
    const close = (e: MouseEvent) => { if (!ref.current?.contains(e.target as Node)) setOpen(false); };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, []);
  const initials = (me.full_name || me.username).split(/[\s.]+/).map((p) => p[0]).join("").slice(0, 2).toUpperCase();
  const signOut = async () => {
    await api("/api/auth/logout", { method: "POST" }).catch(() => undefined);
    try { localStorage.removeItem("rai-me"); } catch { /* ignore */ }
    qc.clear();
    nav("/sign-in");
  };
  const scope = me.access.all_roads ? "All roads" : [...me.access.agencies, ...me.access.counties].join(", ") || "No roads assigned";
  return (
    <div style={{ position: "relative" }} ref={ref}>
      <button className="user-btn" aria-haspopup="menu" aria-expanded={open} onClick={() => setOpen((o) => !o)}>
        <span className="avatar" aria-hidden="true">{initials}</span>
        <span className="user-meta hide-phone">{me.full_name || me.username}<small>{ROLE_LABEL[me.role]}</small></span>
      </button>
      {open ? (
        <div className="popover" role="menu">
          <div style={{ padding: "8px 12px" }}>
            <b>{me.full_name || me.username}</b>
            <p className="muted" style={{ fontSize: 13 }}>{me.username} · {ROLE_LABEL[me.role]}</p>
            {me.role === "viewer" || me.role === "surveyor" ? (
              <p className="muted" style={{ fontSize: 13, marginTop: 4 }}>Road access: {scope}{me.access.extra_roads ? ` + ${me.access.extra_roads} more` : ""}</p>
            ) : null}
          </div>
          <button className="menu-item" role="menuitem" onClick={signOut}><Icon name="logout" size={16} />Sign out</button>
        </div>
      ) : null}
    </div>
  );
}

export function AppShell({ me }: { me: Me }) {
  const [drawer, setDrawer] = useState(false);
  const loc = useLocation();
  useEffect(() => setDrawer(false), [loc.pathname]);
  return (
    <div className="shell">
      <SideNav me={me} open={drawer} onNavigate={() => setDrawer(false)} />
      {drawer ? <div className="scrim" onClick={() => setDrawer(false)} aria-hidden="true" /> : null}
      <div className="main">
        <header className="topbar">
          <button className="btn btn-ghost btn-icon topbar-menu" aria-label="Open menu" onClick={() => setDrawer(true)}><Icon name="menu" /></button>
          <GlobalSearch />
          <div className="topbar-actions">
            <SurveyStatus me={me} />
            <ThemeToggle />
            <UserMenu me={me} />
          </div>
        </header>
        <main className="content" id="main">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
